import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import enAccount from './locales/en/account.json';
import enArtifactRegistry from './locales/en/artifactRegistry.json';
import enAuth from './locales/en/auth.json';
import enBareMetal from './locales/en/bareMetal.json';
import enBareMetalDeployment from './locales/en/bareMetalDeployment.json';
import enBillings from './locales/en/billings.json';
import enBlockStorage from './locales/en/blockStorage.json';
import enCloudCompute from './locales/en/cloudCompute.json';
import enCloudComputeDeployment from './locales/en/cloudComputeDeployment.json';
import enCloudComputeInstance from './locales/en/cloudComputeInstance.json';
import enCloudComputeSecurityGroup from './locales/en/cloudComputeSecurityGroup.json';
import enCommon from './locales/en/common.json';
import enComponents from './locales/en/components.json';
import enContainerEngine from './locales/en/containerEngine.json';
import enContainerEngineInstance from './locales/en/containerEngineInstance.json';
import enCoupon from './locales/en/coupon.json';
import enDashboard from './locales/en/dashboard.json';
import enNavigation from './locales/en/navigation.json';
import enNotifications from './locales/en/notifications.json';
import enProfile from './locales/en/profile.json';
import enProjects from './locales/en/projects.json';
import enSecurity from './locales/en/security.json';
import enSettings from './locales/en/settings.json';
import enSshKeys from './locales/en/sshKeys.json';
import enStatus from './locales/en/status.json';
import enTopup from './locales/en/topup.json';
import enValidation from './locales/en/validation.json';
import thAccount from './locales/th/account.json';
import thArtifactRegistry from './locales/th/artifactRegistry.json';
import thAuth from './locales/th/auth.json';
import thBareMetal from './locales/th/bareMetal.json';
import thBareMetalDeployment from './locales/th/bareMetalDeployment.json';
import thBillings from './locales/th/billings.json';
import thBlockStorage from './locales/th/blockStorage.json';
import thCloudCompute from './locales/th/cloudCompute.json';
import thCloudComputeDeployment from './locales/th/cloudComputeDeployment.json';
import thCloudComputeInstance from './locales/th/cloudComputeInstance.json';
import thCloudComputeSecurityGroup from './locales/th/cloudComputeSecurityGroup.json';
import thCommon from './locales/th/common.json';
import thComponents from './locales/th/components.json';
import thContainerEngine from './locales/th/containerEngine.json';
import thContainerEngineInstance from './locales/th/containerEngineInstance.json';
import thCoupon from './locales/th/coupon.json';
import thDashboard from './locales/th/dashboard.json';
import thNavigation from './locales/th/navigation.json';
import thNotifications from './locales/th/notifications.json';
import thProfile from './locales/th/profile.json';
import thProjects from './locales/th/projects.json';
import thSecurity from './locales/th/security.json';
import thSettings from './locales/th/settings.json';
import thSshKeys from './locales/th/sshKeys.json';
import thStatus from './locales/th/status.json';
import thTopup from './locales/th/topup.json';
import thValidation from './locales/th/validation.json';

export const SUPPORTED_LANGUAGES = ['th', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = 'xver-locale';

export const resources = {
    th: {
        common: thCommon,
        validation: thValidation,
        notifications: thNotifications,
        status: thStatus,
        navigation: thNavigation,
        components: thComponents,
        auth: thAuth,
        dashboard: thDashboard,
        cloudCompute: thCloudCompute,
        cloudComputeDeployment: thCloudComputeDeployment,
        cloudComputeInstance: thCloudComputeInstance,
        cloudComputeSecurityGroup: thCloudComputeSecurityGroup,
        bareMetal: thBareMetal,
        bareMetalDeployment: thBareMetalDeployment,
        containerEngine: thContainerEngine,
        containerEngineInstance: thContainerEngineInstance,
        blockStorage: thBlockStorage,
        artifactRegistry: thArtifactRegistry,
        account: thAccount,
        profile: thProfile,
        projects: thProjects,
        security: thSecurity,
        billings: thBillings,
        sshKeys: thSshKeys,
        coupon: thCoupon,
        settings: thSettings,
        topup: thTopup,
    },
    en: {
        common: enCommon,
        validation: enValidation,
        notifications: enNotifications,
        status: enStatus,
        navigation: enNavigation,
        components: enComponents,
        auth: enAuth,
        dashboard: enDashboard,
        cloudCompute: enCloudCompute,
        cloudComputeDeployment: enCloudComputeDeployment,
        cloudComputeInstance: enCloudComputeInstance,
        cloudComputeSecurityGroup: enCloudComputeSecurityGroup,
        bareMetal: enBareMetal,
        bareMetalDeployment: enBareMetalDeployment,
        containerEngine: enContainerEngine,
        containerEngineInstance: enContainerEngineInstance,
        blockStorage: enBlockStorage,
        artifactRegistry: enArtifactRegistry,
        account: enAccount,
        profile: enProfile,
        projects: enProjects,
        security: enSecurity,
        billings: enBillings,
        sshKeys: enSshKeys,
        coupon: enCoupon,
        settings: enSettings,
        topup: enTopup,
    },
} as const;

const initialLanguage =
    (typeof window !== 'undefined' && window.localStorage?.getItem(LANGUAGE_STORAGE_KEY)) || 'th';

i18n.use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        lng: initialLanguage,
        fallbackLng: 'th',
        supportedLngs: SUPPORTED_LANGUAGES,
        defaultNS: 'common',
        ns: Object.keys(resources.th),
        interpolation: {
            escapeValue: false,
        },
        detection: {
            order: ['localStorage'],
            caches: ['localStorage'],
            lookupLocalStorage: LANGUAGE_STORAGE_KEY,
        },
        react: {
            useSuspense: true,
        },
    });

const syncDocumentLanguage = (language: string) => {
    if (typeof document !== 'undefined') {
        document.documentElement.lang = language;
    }
};

syncDocumentLanguage(i18n.resolvedLanguage ?? i18n.language);
i18n.on('languageChanged', syncDocumentLanguage);

export default i18n;
